
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Laptop, Zap, Globe, Users, TrendingUp, Rocket } from 'lucide-react';

const TechnologyPEO = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-cyan-50">
      <Helmet>
        <title>PEO Services for Technology Companies & Startups | The PEO App</title>
        <meta name="description" content="Specialized PEO services for technology companies, startups, and software companies. Expert HR for rapid scaling, equity compensation, and remote workforce management." />
        <link rel="canonical" href="https://thepeoapp.com/industries/technology" />
        <meta name="keywords" content="technology PEO, startup PEO, software company PEO, tech startup HR, equity compensation, remote workforce PEO, scaling technology companies" />
        
        <meta property="og:title" content="PEO Services for Technology Companies & Startups" />
        <meta property="og:description" content="Specialized PEO solutions for tech companies with rapid scaling support and equity compensation expertise." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/industries/technology" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Laptop className="h-16 w-16 text-cyan-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              PEO Services for Technology Companies
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Specialized Professional Employer Organization services for technology companies, startups, 
              and software firms. Expert HR solutions for rapid scaling, equity compensation, and remote teams.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">Technology Industry Challenges</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <TrendingUp className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Rapid Scaling Needs</h3>
                    <p className="text-gray-700">Going from 10 to 100+ employees quickly while maintaining compliance</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Zap className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Equity Compensation</h3>
                    <p className="text-gray-700">Complex stock options, RSUs, and equity participation programs</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Globe className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Remote Workforce</h3>
                    <p className="text-gray-700">Managing distributed teams across multiple states and countries</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Users className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Talent Competition</h3>
                    <p className="text-gray-700">Competing for top talent with attractive benefits and perks</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Rocket className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Funding Stage Transitions</h3>
                    <p className="text-gray-700">HR needs changing rapidly through seed, Series A, B, and beyond</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Laptop className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Tech-Forward Culture</h3>
                    <p className="text-gray-700">Need for modern, tech-savvy HR platforms and processes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Technology-Specialized PEO Solutions</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-cyan-50 p-6 rounded-xl">
                <TrendingUp className="h-12 w-12 text-cyan-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Rapid Scaling Support</h3>
                <p className="text-gray-700">
                  Infrastructure and processes designed to support quick growth 
                  from startup to enterprise without losing compliance or culture.
                </p>
              </div>
              
              <div className="bg-cyan-50 p-6 rounded-xl">
                <Zap className="h-12 w-12 text-cyan-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Equity Management</h3>
                <p className="text-gray-700">
                  Expert handling of stock options, RSUs, ESPP programs, 
                  and complex equity compensation structures.
                </p>
              </div>
              
              <div className="bg-cyan-50 p-6 rounded-xl">
                <Globe className="h-12 w-12 text-cyan-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Remote Team Management</h3>
                <p className="text-gray-700">
                  Multi-state compliance, remote onboarding, and distributed 
                  team management with modern HR technology platforms.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Find Technology-Focused PEOs</h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with PEO providers who understand the unique needs of technology companies 
              and have proven experience supporting startups through rapid growth phases.
            </p>
            <Link to="/application">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Technology PEO Quotes
              </Button>
            </Link>
          </div>

          <div className="bg-gray-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-6">Technology Companies We Serve</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">SaaS Companies</h3>
                <p className="text-sm text-gray-600">Software as a Service, cloud platforms</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Startups</h3>
                <p className="text-sm text-gray-600">Early stage, seed to Series C companies</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Software Development</h3>
                <p className="text-sm text-gray-600">Custom software, mobile apps, web development</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">IT Services</h3>
                <p className="text-sm text-gray-600">Cybersecurity, cloud services, IT consulting</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TechnologyPEO;
